/**
 * KOPS-596 Task 417: Support Inbox
 * aisearchportal.com/admin/support
 * Navy + Gold split-panel layout (matches ContactsPage pattern)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Button, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { useSocket } from '../../hooks/useSocket';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faComments, faPaperPlane, faUser, faEnvelope,
  faCalendarAlt, faClock, faInbox, faPaperclip, faTimes, faFile, faImage,
  faDownload, faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

interface SupportConversation {
  id: string;
  user_id: string;
  agent_type: string;
  title: string;
  created_at: string;
  updated_at: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  last_message: string | null;
  last_message_time: string | null;
  message_count: number;
}

interface ChatMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: any;
}

// Parse metadata from MySQL (may be a JSON string or already an object)
const parseMetadata = (meta: any) => {
  if (!meta) return {};
  if (typeof meta === 'string') {
    try { return JSON.parse(meta); } catch { return {}; }
  }
  return meta;
};

const parseMessages = (msgs: any[]) =>
  msgs.map(m => ({ ...m, metadata: parseMetadata(m.metadata) }));

// Append auth token to attachment URLs so <img> tags can load them
const getAuthUrl = (url: string) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db'
};

const SupportInboxPage: React.FC = () => {
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  // Removed subscriber dependency — support inbox shows ALL conversations regardless of plan/role
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);

  // Right panel
  const [selected, setSelected] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [fileAttachments, setFileAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch ALL support conversations — anyone who messaged us, regardless of plan/role/status
  const fetchData = useCallback(async () => {
    try {
      const convRes = await axiosInstance.get('/chat/support/conversations');
      const convs: SupportConversation[] = convRes.data.success ? (convRes.data.conversations || []) : [];
      setConversations(convs);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  }, []);

  // Real-time: listen for new support messages via Socket.io
  const { socket } = useSocket({
    onNewMessage: useCallback((msg: any) => {
      // If this message is for the currently selected conversation, add it live
      if (selected && msg.conversation_id === selected.id && msg.role === 'user') {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, {
            id: msg.id,
            conversation_id: msg.conversation_id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            metadata: msg.metadata,
          }];
        });
      }
      // Refresh conversation list to update last_message and sort order
      fetchData();
    }, [selected, fetchData]),
  });

  // Join all support rooms so we get notified of any new message
  useEffect(() => {
    if (!socket) return;
    // Listen on a global support channel
    socket.on('new-support-message', (msg: any) => {
      if (selected && msg.conversation_id === selected.id) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, {
            id: msg.id,
            conversation_id: msg.conversation_id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            metadata: msg.metadata,
          }];
        });
      }
      // Always refresh the list
      fetchData();
    });
    return () => { socket.off('new-support-message'); };
  }, [socket, selected, fetchData]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Poll every 10s as backup
    return () => clearInterval(interval);
  }, [fetchData]);

  // Simple list: every support conversation, sorted by most recent message
  const mergedList = React.useMemo(() => {
    return conversations.map(c => ({
      ...c,
      hasConversation: true,
    } as SupportConversation & { hasConversation: boolean }))
    .sort((a, b) => {
      // Messages first, then by time
      if ((a as any).message_count > 0 && (b as any).message_count === 0) return -1;
      if ((a as any).message_count === 0 && (b as any).message_count > 0) return 1;
      if (a.last_message_time && b.last_message_time) return new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime();
      return 0;
    });
  }, [conversations]);

  // Load messages when conversation selected
  const selectConversation = async (conv: SupportConversation) => {
    setMessagesLoading(true);
    setMessages([]);
    // Leave previous room, join new one
    if (selected && socket) socket.emit('leave', `support-${selected.id}`);
    try {
      const res = await axiosInstance.get(`/chat/support/conversations/${conv.user_id}`);
      if (res.data.success) {
        const realConv = { ...conv, id: res.data.conversation.id };
        setSelected(realConv);
        setMessages(parseMessages(res.data.messages || []));
        // Join socket room for real-time updates
        if (socket) socket.emit('join', `support-${realConv.id}`);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setSelected(conv);
    }
    setMessagesLoading(false);
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll active conversation for new messages every 5 seconds
  useEffect(() => {
    if (!selected) return;
    const pollMessages = async () => {
      try {
        const res = await axiosInstance.get(`/chat/support/conversations/${selected.user_id}`);
        if (res.data.success && res.data.messages) {
          setMessages(prev => {
            const newMsgs = parseMessages(res.data.messages);
            if (newMsgs.length !== prev.length) return newMsgs;
            return prev;
          });
        }
      } catch {}
    };
    const interval = setInterval(pollMessages, 5000);
    return () => clearInterval(interval);
  }, [selected]);

  // Send message (text or text + files)
  const handleSend = async () => {
    if ((!newMessage.trim() && fileAttachments.length === 0) || !selected) return;
    setSending(true);
    const text = newMessage.trim();
    const filesToSend = [...fileAttachments];
    setFileAttachments([]);
    try {
      let res;
      if (filesToSend.length > 0) {
        // Send files directly via support endpoint
        const formData = new FormData();
        formData.append('conversationId', selected.id);
        formData.append('message', text || `Sent ${filesToSend.length} file(s)`);
        formData.append('subscriberUserId', selected.user_id);
        filesToSend.forEach(file => {
          if (file.type.startsWith('image/')) formData.append('images', file);
          else formData.append('files', file);
        });
        res = await axiosInstance.post('/chat/support/send', formData);
        if (res.data.success) {
          setMessages(prev => [...prev, res.data.message]);
        }
      } else {
        res = await axiosInstance.post('/chat/support/send', {
          conversationId: selected.id,
          message: text,
          subscriberUserId: selected.user_id
        });
        if (res.data.success) {
          setMessages(prev => [...prev, res.data.message]);
        }
      }
      setNewMessage('');
      setConversations(prev => prev.map(c =>
        c.id === selected.id
          ? { ...c, last_message: text || 'Sent file(s)', last_message_time: new Date().toISOString(), message_count: (c.message_count || 0) + 1 }
          : c
      ));
    } catch {
      setAlert({ type: 'danger', message: 'Failed to send message' });
    }
    setSending(false);
  };

  // Filter merged list by search
  const filtered = mergedList.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.user_first_name || '').toLowerCase().includes(q) ||
      (c.user_last_name || '').toLowerCase().includes(q) ||
      (c.user_email || '').toLowerCase().includes(q) ||
      (c.last_message || '').toLowerCase().includes(q)
    );
  });

  const timeAgo = (dateStr: string | null) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div style={{ background: colors.navy, minHeight: '100vh' }}>
      {/* Top Nav */}
      <nav style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '12px 0' }}>
        <Container fluid className="d-flex justify-content-between align-items-center" style={{ maxWidth: '1400px' }}>
          <div>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal
              </span>
            </a>
            <span style={{ color: colors.muted, fontSize: '14px', marginLeft: '16px' }}>/ Support Inbox</span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a href="/admin/subscribers" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Subscribers</a>
            <a href="/admin/contacts" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Contacts</a>
            <a href="/admin/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Events</a>
            <a href="/admin/support" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '13px', padding: '6px 12px', fontWeight: 600 }}>Support</a>
            <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Dashboard</a>
          </div>
        </Container>
      </nav>

      {alert && (
        <div style={{ padding: '0 24px', paddingTop: '12px' }}>
          <Alert variant={alert.type as any} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>
        </div>
      )}

      {/* Split Panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 60px)' }}>
        {/* Left Panel — Conversation List */}
        <div style={{ width: '380px', borderRight: `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column' }}>
          {/* Search */}
          <div style={{ padding: '16px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '12px', top: '10px', color: colors.muted, fontSize: '13px' }} />
              <Form.Control
                type="text"
                placeholder="Search conversations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ background: colors.navy3, border: 'none', color: colors.white, paddingLeft: '36px', fontSize: '13px', borderRadius: '8px' }}
              />
            </div>
            <div style={{ fontSize: '11px', color: colors.muted, marginTop: '8px' }}>
              {filtered.length} subscriber{filtered.length !== 1 ? 's' : ''} · {conversations.length} active conversation{conversations.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Conversation List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spinner size="sm" style={{ color: colors.gold }} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: colors.muted, fontSize: '13px' }}>
                <FontAwesomeIcon icon={faInbox} style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }} /><br />
                No subscribers found
              </div>
            ) : (
              filtered.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: `1px solid ${colors.navy3}`,
                    cursor: 'pointer',
                    background: selected?.id === conv.id ? colors.navy3 : 'transparent',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => { if (selected?.id !== conv.id) (e.currentTarget.style.background = colors.navy2); }}
                  onMouseLeave={e => { if (selected?.id !== conv.id) (e.currentTarget.style.background = 'transparent'); }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: colors.white, fontSize: '13px', marginBottom: '2px' }}>
                        {conv.user_first_name} {conv.user_last_name}
                      </div>
                      <div style={{ fontSize: '11px', color: colors.muted, marginBottom: '6px' }}>
                        {conv.user_email}
                      </div>
                      {conv.last_message ? (
                        <div style={{ fontSize: '12px', color: colors.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {conv.last_message.substring(0, 80)}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: colors.navy3, fontStyle: 'italic' }}>No messages yet — click to start</div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                      <div style={{ fontSize: '10px', color: colors.muted }}>
                        {timeAgo(conv.last_message_time || conv.updated_at)}
                      </div>
                      {conv.message_count > 0 && (
                        <Badge bg="" style={{ background: colors.gold, color: colors.navy, fontSize: '10px', marginTop: '4px' }}>
                          {conv.message_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel — Chat */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: colors.navy }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <FontAwesomeIcon icon={faComments} style={{ fontSize: '48px', color: colors.navy3, marginBottom: '16px' }} />
              <div style={{ color: colors.muted, fontSize: '14px' }}>Select a conversation to view messages</div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.navy3}`, background: colors.navy2 }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div style={{ fontWeight: 700, color: colors.white, fontSize: '15px' }}>
                      <FontAwesomeIcon icon={faUser} className="me-2" style={{ color: colors.gold }} />
                      {selected.user_first_name} {selected.user_last_name}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.muted, marginTop: '2px' }}>
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />{selected.user_email}
                      <span style={{ margin: '0 8px' }}>·</span>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />Opened {new Date(selected.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Button size="sm" variant="outline-light" style={{ fontSize: '11px' }}
                    onClick={() => window.open(`/admin/subscribers?search=${encodeURIComponent(selected.user_email)}`, '_blank')}>
                    View Subscriber
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f8f9fa' }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spinner size="sm" /> Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 0', fontSize: '13px' }}>
                    No messages in this conversation yet.
                  </div>
                ) : (
                  messages.map(msg => (
                    <div key={msg.id} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.role === 'assistant' ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '65%', overflow: 'hidden',
                        borderRadius: msg.role === 'assistant' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        background: msg.role === 'assistant' ? colors.navy : '#fff',
                        color: msg.role === 'assistant' ? '#fff' : '#333',
                        border: msg.role === 'assistant' ? 'none' : '1px solid #e0e0e0',
                        fontSize: '13px', lineHeight: 1.5
                      }}>
                        <div style={{ padding: '10px 14px 0' }}>
                          <div style={{ fontSize: '10px', fontWeight: 600, marginBottom: '4px', color: msg.role === 'assistant' ? colors.gold : '#999' }}>
                            {msg.role === 'assistant' ? 'KairosIQ Support' : `${selected.user_first_name}`}
                          </div>
                        </div>
                        {/* Image attachments — full-width inside bubble */}
                        {(() => {
                          const atts = msg.metadata?.attachments || [];
                          const images = atts.filter((a: any) => a.type === 'image' || a.mimetype?.startsWith('image/'));
                          const files = atts.filter((a: any) => !(a.type === 'image' || a.mimetype?.startsWith('image/')));
                          return (
                            <>
                              {images.length > 0 && (
                                <div style={{ padding: '4px 0 0' }}>
                                  {images.map((att: any, i: number) => {
                                    const fileKey = att.s3Key || att.savedFileName || att.filename || '';
                                    const fileName = att.name || att.originalName || 'Image';
                                    const url = getAuthUrl(`/api/chat/attachments/${encodeURIComponent(fileKey)}`);
                                    return (
                                      <a key={i} href={url} target="_blank" rel="noreferrer" style={{ display: 'block', lineHeight: 0 }}>
                                        <img src={url} alt={fileName} style={{
                                          width: '100%', maxHeight: 280, objectFit: 'cover', display: 'block', cursor: 'pointer',
                                        }} />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                              {files.length > 0 && (
                                <div style={{ padding: '6px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {files.map((att: any, i: number) => {
                                    const fileKey = att.s3Key || att.savedFileName || att.filename || '';
                                    const fileName = att.name || att.originalName || 'File';
                                    const url = getAuthUrl(`/api/chat/attachments/${encodeURIComponent(fileKey)}`);
                                    const isPdf = att.mimetype === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
                                    const fileSize = att.size ? (att.size > 1048576 ? `${(att.size / 1048576).toFixed(1)} MB` : `${Math.round(att.size / 1024)} KB`) : '';
                                    return (
                                      <a key={i} href={url} target="_blank" rel="noreferrer" style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                        background: msg.role === 'assistant' ? 'rgba(255,255,255,0.08)' : '#f8f9fa',
                                        borderRadius: 8, textDecoration: 'none',
                                        border: msg.role === 'assistant' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e9ecef',
                                      }}>
                                        <div style={{
                                          width: 32, height: 32, borderRadius: 6,
                                          background: isPdf ? '#e74c3c' : colors.gold,
                                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}>
                                          <FontAwesomeIcon icon={isPdf ? faFilePdf : faFile} style={{ color: '#fff', fontSize: 14 }} />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{ fontSize: 12, fontWeight: 600, color: msg.role === 'assistant' ? '#fff' : '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</div>
                                          {fileSize && <div style={{ fontSize: 10, color: msg.role === 'assistant' ? 'rgba(255,255,255,0.5)' : '#999', marginTop: 1 }}>{fileSize}</div>}
                                        </div>
                                        <FontAwesomeIcon icon={faDownload} style={{ color: msg.role === 'assistant' ? colors.gold : '#999', fontSize: 12, flexShrink: 0 }} />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {/* Text content — hide "Sent X file(s)" when attachments are present */}
                        {msg.content && !((msg.metadata?.attachments?.length > 0) && /^Sent \d+ file\(s\)$/.test(msg.content)) && (
                          <div style={{ padding: '4px 14px' }}>{msg.content}</div>
                        )}
                        <div style={{ fontSize: '10px', padding: '4px 14px 10px', opacity: 0.6 }}>
                          {new Date(msg.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* File preview */}
              {fileAttachments.length > 0 && (
                <div style={{ borderTop: '1px solid #e9ecef', padding: '8px 20px', background: '#f8f9fa', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {fileAttachments.map((file, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', fontSize: 11 }}>
                      <FontAwesomeIcon icon={file.type.startsWith('image/') ? faImage : faFile} style={{ color: colors.gold, fontSize: 10 }} />
                      <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                      <FontAwesomeIcon icon={faTimes} style={{ cursor: 'pointer', color: '#999', fontSize: 10 }}
                        onClick={() => setFileAttachments(prev => prev.filter((_, j) => j !== i))} />
                    </div>
                  ))}
                </div>
              )}

              {/* Message Input */}
              <div style={{ borderTop: '1px solid #e9ecef', padding: '14px 20px', background: '#fff', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple
                  onChange={(e) => { if (e.target.files) setFileAttachments(prev => [...prev, ...Array.from(e.target.files!)]); e.target.value = ''; }} />
                <button onClick={() => fileInputRef.current?.click()}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', color: colors.navy, fontSize: 16 }}>
                  <FontAwesomeIcon icon={faPaperclip} />
                </button>
                <Form.Control
                  type="text"
                  placeholder="Type a reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && (newMessage.trim() || fileAttachments.length > 0)) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  style={{ fontSize: '13px' }}
                  disabled={sending}
                />
                <Button
                  style={{ background: colors.navy, border: 'none', whiteSpace: 'nowrap', padding: '8px 20px' }}
                  disabled={sending || (!newMessage.trim() && fileAttachments.length === 0)}
                  onClick={handleSend}
                >
                  <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Send
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportInboxPage;
